<template>
  <div class="text-center mt-4 px-4 px-md-0">
    <h1 class="text-h4 font-weight-black mb-4">
      {{ $t('registration.website.greeting', { username: $auth.user.name }) }}
    </h1>
    <p class="text-body-2 mb-8">
      {{ $t('registration.website.subheadline', { platform: readablePlatformName }) }}
    </p>

    <v-row class="ma-0 pa-0">
      <v-select
        v-model="countryCode"
        variant="outlined"
        :label="$t('registration.website.labels.country')"
        :items="availableCountryItems"
        item-title="text"
        item-value="value"
      />
    </v-row>

    <LinkData
      v-bind="{ links }"
      :is-billwerk-active="isBillwerkActive"
      :trigger-validation="triggerLinkValidation"
      @updateHasNoInitialWebsite="updateHasNoInitialWebsite"
      @resetTriggerValidation="setTriggerLinkValidation(false)"
      @updateSettings="updateLinks"
      @validated="submit"
    />
    <div
      v-if="!isBillwerkActive"
      class="ml-1"
    >
      <v-checkbox
        v-model="termsCheckbox"
        color="primary"
        density="compact"
        :rules="[rules.required]"
      >
        <template v-slot:label>
          <span style="text-align: left; padding-left: 10px">
            <Terms />
          </span>
        </template>
      </v-checkbox>
    </div>
    <v-btn
      class="w-full primary mb-2 mt-4"
      :disabled="!canSubmit || (!isBillwerkActive && !termsCheckbox)"
      :loading="isLoading"
      @click="setTriggerLinkValidation(true)"
    >
      {{ $t('buttons.signup.try-free') }}
    </v-btn>
    <RemaxInfoDialog
      :is-de-remax-customer="isDeRemaxCustomer"
      @accept="reset"
    />
  </div>
</template>

<script>
import bus, { eventNames } from '@/lib/eventBus'
import { formatHttpsUrl } from '@/lib/urlFormatter'
import { isValidUrl, required } from '@/lib/validation'
import brandingMixin from '@/mixins/branding'
import countries from '@/mixins/countries'
import Terms from '@/modules/auth/Register/Terms.vue'
import LinkData from '@/modules/registration/LinkData.vue'

import RemaxInfoDialog from '../auth/RemaxInfoDialog.vue'
import COMPLETE_ONBOARDING_STEP_WEBSITE from './queries/CompleteOnboardingStepWebsite.gql'

export default {
  components: {
    Terms,
    LinkData,
    RemaxInfoDialog,
  },
  mixins: [brandingMixin, countries],
  props: {
    isBillwerkActive: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      links: {
        website: '',
        privacy: '',
        imprint: '',
      },
      isLoading: false,
      hasNoInitialWebsite: false,
      countryCode: '',
      triggerLinkValidation: false,
      termsCheckbox: false,
    }
  },
  computed: {
    isDeRemaxCustomer() {
      return (
        this.countryCode === 'DE' &&
        Boolean(
          this.links.website.match(/remax/i) ||
            this.links.privacy.match(/remax/i) ||
            this.links.imprint.match(/remax/i)
        )
      )
    },
    canSubmit() {
      return (
        this.hasNoInitialWebsite ||
        Object.keys(this.links).every((key) => isValidUrl(this.links[key]))
      )
    },
  },
  created() {
    this.rules = { required }
    const { locale = 'de-DE' } = this.$auth.user
    const { value } =
      this.availableCountryItems.find((item) => item.value === locale.split('-')[1]) || {}
    this.countryCode = value || this.availableCountryItems[0].value
  },
  methods: {
    reset() {
      this.links = {
        website: '',
        privacy: '',
        imprint: '',
      }
    },
    updateLinks(links) {
      this.links = { ...links }
    },
    updateHasNoInitialWebsite(hasNoInitialWebsite) {
      this.hasNoInitialWebsite = hasNoInitialWebsite
    },
    setTriggerLinkValidation(trigger) {
      this.triggerLinkValidation = trigger
    },

    async submit() {
      if (!this.hasNoInitialWebsite) {
        Object.keys(this.links).forEach((key) => {
          this.links[key] = formatHttpsUrl(this.links[key])
        })
      }

      try {
        this.isLoading = true
        const {
          data: { completeOnboardingStepWebsite },
        } = await this.$apollo.mutate({
          mutation: COMPLETE_ONBOARDING_STEP_WEBSITE,
          variables: {
            input: {
              hasNoInitialWebsite: this.hasNoInitialWebsite,
              links: this.links,
              countryCode: this.countryCode.toLowerCase(),
            },
          },
        })
        if (completeOnboardingStepWebsite.status === 'SUCCESS') {
          this.$emit('completed', this.countryCode.toLowerCase())
        }

        if (completeOnboardingStepWebsite.status === 'ERROR') {
          if (completeOnboardingStepWebsite.errorCode === 'INVALID_WEBSITE_LINKS') {
            bus.$emit(eventNames.SHOW_SNACKBAR, {
              color: 'error',
              text: this.$t('registration.website.errors.invalid-link'),
            })
          } else {
            bus.$emit(eventNames.SHOW_SNACKBAR, {
              color: 'error',
              text: this.$t('registration.website.errors.other'),
            })
          }
        }
      } catch (error) {
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'error',
          text: this.$t('registration.website.errors.other'),
        })
        this.isLoading = false
      }
    },
  },
  emits: ['completed'],
  watch: {
    termsCheckbox(val) {
      if (val) {
        this.$tracking.event('Account Creation', 'Clicked', 'AGB DSB Read and AGB Accepted')
      }
    },
  },
}
</script>
